import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Box,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';
import { toast } from 'react-toastify';

export default function SmartWorkingModal({ open, onClose, userId, userName }) {
  const [enabled, setEnabled] = useState(false);
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && userId) {
      fetchSettings();
    } else {
        // Reset state when closed or no user
        setEnabled(false);
        setIps([]);
        setNewIp('');
        setError('');
    }
  }, [open, userId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/users/${userId}/smart-working`);
      setEnabled(res.data.enabled || false);
      let loadedIps = res.data.ips;
      // Parse if string (should be object from backend, but safe check)
      if (typeof loadedIps === 'string') {
          try { loadedIps = JSON.parse(loadedIps); } catch(e) { loadedIps = []; }
      }
      setIps(Array.isArray(loadedIps) ? loadedIps : []);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento impostazioni Smart Working');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put(`/users/${userId}/smart-working`, {
        enabled,
        ips
      });
      toast.success('Impostazioni Smart Working aggiornate');
      onClose();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message;
      toast.error(msg || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIp = () => {
    if (!newIp) return;
    // Simple IP validation regex (IPv4)
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(newIp)) {
        setError('Indirizzo IP non valido');
        return;
    }
    if (ips.includes(newIp)) {
        setError('IP già presente');
        return;
    }
    setIps([...ips, newIp]);
    setNewIp('');
    setError('');
  };

  const handleRemoveIp = (ipToRemove) => {
    setIps(ips.filter(ip => ip !== ipToRemove));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Smart Working - {userName}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Abilita Smart Working (Restrizione IP)"
          />
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Indirizzi IP Autorizzati
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="es. 192.168.1.1"
                value={newIp}
                onChange={(e) => {
                    setNewIp(e.target.value);
                    setError('');
                }}
                error={!!error}
                helperText={error}
              />
              <Button 
                variant="contained" 
                onClick={handleAddIp}
                disabled={!newIp}
                startIcon={<AddIcon />}
              >
                Aggiungi
              </Button>
            </Box>

            <List dense sx={{ border: '1px solid #eee', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              {ips.length === 0 && (
                <ListItem>
                  <ListItemText secondary="Nessun IP configurato" />
                </ListItem>
              )}
              {ips.map((ip) => (
                <ListItem key={ip}>
                  <ListItemText primary={ip} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveIp(ip)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            
            {enabled && ips.length === 0 && (
               <Alert severity="warning" sx={{ mt: 2 }}>
                 Attenzione: Smart Working abilitato ma nessun IP configurato. L'accesso sarà bloccato per questo utente da qualsiasi IP esterno.
               </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}
